import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import './LitePanel.scss';
import { useQuery } from '@apollo/react-hooks';
import { Queries } from 'gql';
import HamburgerMenuIcon from '../icons/HamburgerMenu';
import { useCurrentProject } from 'providers/LiteProjectProvider';
import Button from '../Button';
import { Menu, MenuButton, MenuItem } from '@szhsin/react-menu';
import NvidiaLogo from 'static/img/nvidia-logo.png';
import DellLogo from 'static/img/dell-logo.png';
import HpeLogo from 'static/img/hpe-logo.png';
import OpenLink from '../icons/OpenLink';
import awsBadge from 'static/img/aws2.png';
import azBadge from 'static/img/az.png';
import gcpBadge from 'static/img/gcp.png';
import ociBadge from 'static/img/oci2.png';
import DoubleArrowIcon from '../icons/DoubleArrow';
import TriangleIcon from '../icons/Triangle';
import {
  cloudEnvironmentOptions,
  industryOptions,
  multicloudDriverOptions,
  regionOptions,
  strategicGoalOptions,
  userScaleOptions,
} from 'views/LiteView/constants';
import CheckCircleIcon from '../icons/CheckCircle';

const formatPhaseMenuName = (phaseName) => {
  const words = String(phaseName || '').split(' ');
  if (words.length !== 2) return phaseName;

  return `${words[0]} ${words[1].toLowerCase()}`;
};

const LitePanel = ({ onRampsMetros, metros = [], onMapFocusChange, marketplaceSegment }) => {
  const { project, projectId, phase, changePhase, phaseLoading } = useCurrentProject();
  const projectCreatedOn = project?.createdOn?.toString ? project.createdOn.toString('MMMM D, YYYY') : null;

  const onRamps = {
    AWS: { name: 'AWS Direct Connect', logo: awsBadge },
    Azure: { name: 'Azure ExpressRoute', logo: azBadge },
    Google: { name: 'Google Cloud Interconnect', logo: gcpBadge },
    Oracle: { name: 'Oracle FastConnect', logo: ociBadge },
  };
  const cspByServiceLogo = {
    AWS: { name: 'AWS', logo: awsBadge },
    Azure: { name: 'Microsoft Azure', logo: azBadge },
    GoogleCloud: { name: 'Google Cloud', logo: gcpBadge },
    Oracle: { name: 'Oracle Cloud', logo: ociBadge },
  };
  const [isFirstSection, setIsFirstSection] = useState(true);
  const [panelIndex, setPanelIndex] = useState(0);
  const { data: liteFormData } = useQuery(Queries.GET_WORKBENCH_LITE_FORM, {
    variables: { projectId },
    skip: !projectId,
  });
  const phases = project?.phases || [];
  const activePhaseId = phase?.id?.value;
  const contactSectionIndex = phases.length + 1;
  const appendixSectionIndex = phases.length + 2;
  const panelMaxIndex = appendixSectionIndex;
  const lastPhaseIndex = Math.max(phases.length - 1, 0);
  const isIntroSection = panelIndex === 0;
  const isContactSection = panelIndex === contactSectionIndex;
  const isAppendixSection = panelIndex === appendixSectionIndex;
  const isSingleSection = isIntroSection || isContactSection || isAppendixSection;
  const panelPhaseIndex = phases.length === 0 ? -1 : isSingleSection ? lastPhaseIndex : panelIndex - 1;
  const panelPhaseId = panelPhaseIndex >= 0 ? phases[panelPhaseIndex]?.id?.value : null;
  const phaseMetros = phase?.metros || [];
  const allMetros = Array.isArray(metros) ? metros : [];
  const getMetroCode = (metro) => metro?.code?.value || metro?.code;
  const getMetroNotes = (metro) => (metro?.notes || '').trim().toUpperCase();
  const getMarketplaceDensityRows = (metro) => {
    const buyers = metro?.marketplace?.buyers || [];

    if (!marketplaceSegment) return [];

    if (marketplaceSegment.name === 'All Participants') {
      const allCount = buyers.reduce((sum, { count }) => sum + (count || 0), 0);
      return [{ name: marketplaceSegment.name, count: allCount }];
    }

    const segmentMatch = buyers.find((buyer) => buyer.name === marketplaceSegment.name);
    if (segmentMatch) {
      return [{ name: segmentMatch.name, count: segmentMatch.count }];
    }

    const subSegmentRows = buyers
      .map((buyer) => buyer?.subSegments?.find((sub) => sub.name === marketplaceSegment.name))
      .filter((sub) => !!sub)
      .map((sub) => ({ name: sub.name, count: sub.count }));

    return subSegmentRows;
  };
  const phaseHqMetro = phaseMetros.find((metro) => getMetroNotes(metro) === 'HQ') || phaseMetros.find((metro) => getMetroCode(metro) === 'HQ');

  const getMetroIdValue = (metro) => metro?.id?.value || metro?.id;
  const phaseMetroIds = new Set(phaseMetros.map((metro) => getMetroIdValue(metro)));
  const phaseCatalogMetros = allMetros.filter((metro) => phaseMetroIds.has(getMetroIdValue(metro)));

  const hqMetro =
    phaseCatalogMetros.find((metro) => getMetroIdValue(metro) === getMetroIdValue(phaseHqMetro)) ||
    phaseCatalogMetros.find((metro) => getMetroNotes(metro) === 'HQ') ||
    phaseCatalogMetros.find((metro) => getMetroCode(metro) === 'HQ');

  const hqMetroLatencies = hqMetro
    ? hqMetro
        .getPaths(phaseCatalogMetros, allMetros)
        .tos.filter((to) => !to.metro.id.equals(hqMetro.id))
        .sort((a, b) => a.distance - b.distance)
    : [];
  useEffect(() => {
    if (isSingleSection && !isFirstSection) {
      setIsFirstSection(true);
    }
  }, [isSingleSection, isFirstSection]);

  useEffect(() => {
    if (panelIndex > panelMaxIndex) {
      setPanelIndex(panelMaxIndex);
    }
  }, [panelIndex, panelMaxIndex]);

  useEffect(() => {
    if (!panelPhaseId || panelPhaseId === activePhaseId) return;
    changePhase(panelPhaseId);
  }, [panelPhaseId, activePhaseId, changePhase]);

  const handlePhaseSelect = (phaseId) => {
    setIsFirstSection(true);
    const selectedIndex = phases.findIndex((phaseItem) => phaseItem.id.value === phaseId);
    const nextPanelIndex = selectedIndex >= 0 ? selectedIndex + 1 : 1;
    setPanelIndex(nextPanelIndex);
    if (phaseId && phaseId !== activePhaseId) {
      changePhase(phaseId);
    }
  };

  const phaseSectionOrder = [
    { key: 'Core Location', label: 'Placing infrastructure to optimize performance and compliance' },
    { key: 'Interconnections', label: 'Connecting clouds and locations through private interconnection' },
    { key: 'Edge Locations', label: 'Extending reach to edge markets efficiently' },
    { key: 'Low Latency', label: 'Designing for low-latency performance' },
    { key: 'Potential Expansion', label: 'Expanding into locations where your ecosystem is most active' },
  ];

  const hqMetroCodes = phaseMetros
    .filter((metro) => getMetroNotes(metro) === 'HQ')
    .map((metro) => getMetroCode(metro))
    .filter((code) => !!code);
  const metroCodesWithOnRamps = phaseMetros
    .filter((metro) => {
      const metroCode = getMetroCode(metro);
      return onRampsMetros?.some((onRamp) => {
        const onRampMetroCode = onRamp?.metroCode?.value || onRamp?.metroCode;
        return onRampMetroCode === metroCode;
      });
    })
    .map((metro) => getMetroCode(metro))
    .filter((code) => !!code);
  const metroCodesWithInfrastructureLabels = phaseMetros
    .filter((metro) => !!metro?.notes)
    .map((metro) => getMetroCode(metro))
    .filter((code) => !!code);
  const edgeInfrastructureMetro = phaseMetros.find(
    (metro) =>
      getMetroNotes(metro) === 'EDGE' && metro?.infraComponents?.some((infraComponent) => infraComponent.deployment === 'EDGE' && infraComponent.isEnabled)
  );
  const interconnectionSecondaryCodes = Array.from(new Set([...hqMetroCodes, ...metroCodesWithOnRamps, ...metroCodesWithInfrastructureLabels]));
  const lowLatencySecondaryCodes = Array.from(new Set(hqMetroCodes));
  const getMetroServiceLatencyRows = (metro) =>
    (metro?.services || [])
      .map((serviceConnection) => {
        const enabledRegionIds = new Set((serviceConnection?.regions || []).map((region) => region?.id?.value || region?.id));
        let matchedRegionCount = 0;
        const totalLatency = (serviceConnection?.service?.regions || []).reduce((sum, region) => {
          const regionId = region?.id?.value || region?.id;
          if (!enabledRegionIds.has(regionId)) return sum;

          const metroConnection = (region?.connectedMetros || []).find((connectedMetro) => {
            const connectedMetroId = connectedMetro?.metroId?.value || connectedMetro?.metro?.id?.value || connectedMetro?.metro?.id;
            return connectedMetroId === getMetroIdValue(metro);
          });

          if (metroConnection) {
            matchedRegionCount += 1;
          }

          return sum + (metroConnection?.latency || 0);
        }, 0);

        if (enabledRegionIds.size === 0 || matchedRegionCount === 0) return null;

        return {
          id:
            serviceConnection?.id?.value ||
            serviceConnection?.id ||
            `${getMetroIdValue(metro)}-${serviceConnection?.service?.id?.value || serviceConnection?.service?.id}`,
          serviceName: serviceConnection?.service?.name,
          csp: cspByServiceLogo[serviceConnection?.service?.logo],
          totalLatency,
        };
      })
      .filter((serviceLatencyRow) => !!serviceLatencyRow);
  const lowLatencyRows = phaseMetros.flatMap((metro) =>
    getMetroServiceLatencyRows(metro).map((serviceLatencyRow) => ({
      metroName: metro?.name,
      ...serviceLatencyRow,
    }))
  );

  const sectionMapFocusConfig = {
    'Introduction': { mode: 'maxZoomOut' },
    'Core Location': {
      true: hqMetroCodes.length ? { mode: 'metroCodes', metroCodes: hqMetroCodes } : { mode: 'all' },
      false: { mode: 'all' },
    },
    'Interconnections': {
      true: hqMetroCodes.length ? { mode: 'metroCodes', metroCodes: hqMetroCodes } : { mode: 'all' },
      false: interconnectionSecondaryCodes.length ? { mode: 'metroCodes', metroCodes: interconnectionSecondaryCodes } : { mode: 'all' },
    },
    'Edge Locations': {
      true: { mode: 'all' },
      false: edgeInfrastructureMetro ? { mode: 'metroCode', metroCode: getMetroCode(edgeInfrastructureMetro) } : { mode: 'all' },
    },
    'Low Latency': {
      true: { mode: 'all' },
      false: lowLatencySecondaryCodes.length ? { mode: 'metroCodes', metroCodes: lowLatencySecondaryCodes } : { mode: 'all' },
    },
    'Potential Expansion': {
      true: { mode: 'all' },
      false: { mode: 'all' },
    },
    'Contact us': { mode: 'maxZoomOut' },
    'Appendix': { mode: 'maxZoomOut' },
  };

  const liteForm = liteFormData?.getWorkbenchLiteForm;
  const optionLabelByValue = (options) =>
    options.reduce((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
  const cloudEnvironmentLabelByValue = optionLabelByValue(cloudEnvironmentOptions);
  const multicloudDriverLabelByValue = optionLabelByValue(multicloudDriverOptions);
  const industryLabelByValue = optionLabelByValue(industryOptions);
  const userScaleLabelByValue = optionLabelByValue(userScaleOptions);
  const regionLabelByValue = optionLabelByValue(regionOptions);
  const strategicGoalLabelByValue = optionLabelByValue(strategicGoalOptions);
  const formatArrayAnswer = (values, labelsByValue) => {
    if (!Array.isArray(values) || values.length === 0) return [];
    return values.map((value) => labelsByValue[value] || value);
  };
  const formatAnswerRows = (answer, isMultiline) => {
    if (Array.isArray(answer)) return answer.length > 0 ? answer : ['No answer provided'];
    if (!answer) return ['No answer provided'];
    if (isMultiline) return String(answer).split('\n');
    return [answer];
  };
  const headquarterLabel = liteForm?.headquarter?.address?.label;
  const appendixQuestions = [
    {
      question: 'How many cloud and SaaS providers does your organization use?',
      answer: liteForm?.providerCount > 100 ? '100+' : liteForm?.providerCount ?? 'No answer provided',
    },
    {
      question: 'What are the most critical cloud environments for your business?',
      answer: formatArrayAnswer(liteForm?.cloudEnvironments, cloudEnvironmentLabelByValue),
    },
    {
      question: 'What factors are driving your hybrid multicloud approach?',
      answer: formatArrayAnswer(liteForm?.factors, multicloudDriverLabelByValue),
    },
    {
      question: "What is your organization's primary industry vertical?",
      answer: liteForm?.ecosystemDensity ? industryLabelByValue[liteForm.ecosystemDensity] || liteForm.ecosystemDensity : 'No answer provided',
    },
    {
      question: 'How many users do you support across your infrastructure?',
      answer: liteForm?.userScale ? userScaleLabelByValue[liteForm.userScale] || liteForm.userScale : 'No answer provided',
    },
    {
      question: 'Where are your users located globally?',
      answer: liteForm?.userConcentrationRegions
        ? [
            `Highest concentration: ${
              regionLabelByValue[liteForm.userConcentrationRegions.highest] || liteForm.userConcentrationRegions.highest || 'No answer provided'
            }`,
            `High concentration: ${formatArrayAnswer(liteForm.userConcentrationRegions.high, regionLabelByValue).join(', ')}`,
            `Emerging locations: ${formatArrayAnswer(liteForm.userConcentrationRegions.emerging, regionLabelByValue).join(', ')}`,
          ].join('\n')
        : 'No answer provided',
      isMultiline: true,
    },
    {
      question: 'What are your primary strategic goals?',
      answer: formatArrayAnswer(liteForm?.strategicGoals, strategicGoalLabelByValue),
    },
    {
      question: 'Where is your organization headquartered?',
      answer: headquarterLabel || 'No answer provided',
    },
  ];

  const sections = {
    'Introduction': {
      header: { true: 'Design your infrastructure for hybrid multicloud and AI' },
      content: {
        true: (
          <div>
            <p>
              Based on your inputs, this report outlines a recommended distributed architecture that you can build on Equinix Connected Cloud to improve
              performance, control, and scalability across your environments.
            </p>
            <p>The design focuses on five priorities—click the sections below or Continue to see how this applies to your organization:</p>
            <div className="phase-list" role="list">
              {phaseSectionOrder.slice(0, phases.length).map((phaseMeta, index) => (
                <button
                  key={`intro-phase-${phases[index]?.id?.value || index}`}
                  type="button"
                  className="phase-list-item"
                  role="listitem"
                  onClick={() => handlePhaseSelect(phases[index]?.id?.value)}>
                  <span className="phase-list-number">{index + 1}.</span>
                  <span className="phase-list-text">{phaseMeta.label}</span>
                  <span className="phase-list-arrow" aria-hidden="true">
                    →
                  </span>
                </button>
              ))}
            </div>
            <div className={'separator-line'} />
            <p className="small-text">
              This tailored report was auto-generated
              {projectCreatedOn ? ` on ${projectCreatedOn}` : ''} based on the information provided during the Connectivity Advisor survey. For reference, your
              survey responses can be found{' '}
              <a
                href="#appendix"
                onClick={(event) => {
                  event.preventDefault();
                  setPanelIndex(appendixSectionIndex);
                }}>
                here
              </a>
              . To learn more or retake the survey, visit{' '}
              <a href="https://www.equinix.com/connectivity-advisor" target="_blank" rel="noopener noreferrer">
                Connectivity Advisor
              </a>
              .
            </p>
          </div>
        ),
      },
      isSingleSection: true,
    },
    'Core Location': {
      header: {
        true: 'Place infrastructure where it optimizes performance and compliance',
        false: 'Metro locations recommended as primary hubs based on your inputs',
      },
      content: {
        true: (
          <div>
            <h4>Solving for proximity and sovereignty</h4>
            <p>
              Infrastructure placement directly impacts latency, user experience, and compliance with regional data requirements. Deploying infrastructure
              closer to users and data sources reduces response times and simplifies adherence to local regulations.
            </p>
            <h4>High-performance data centers</h4>
            <p>
              Equinix operates 280 high-performance data centers across 77 major metros in 36 countries. With high-density cooling capabilities and support for
              30+ kW per cabinet, our facilities are purpose-built to handle the most demanding AI deployments.
            </p>
            <div className={'separator-line'} />
            <h4>Distributed AI</h4>
            <p>
              A distributed AI architecture leverages a combination of cloud, AI infrastructure, and data center resources across multiple locations. This
              approach enables you to:
              <ul>
                <li>Place workloads where they deliver maximum value</li>
                <li>Run training where compute is abundant</li>
                <li>Keep sensitive processing where privacy and sovereignty is protected</li>
                <li>Perform inference close to where data is generated</li>
              </ul>
              Learn more about{' '}
              <a href="https://www.equinix.com/product-solutions/ai" target="_blank" rel="noopener noreferrer">
                distributed AI
              </a>
              , the AI ecosystem at Equinix and our partnerships with the world’s leading providers.
            </p>
            <a className="brand-row" href="https://www.equinix.com/partners/nvidia" target="_blank" rel="noopener noreferrer">
              <img src={NvidiaLogo} alt="NVIDIA Logo" />
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </a>
            <a className="brand-row" href="https://www.equinix.com/partners/delltechnologies" target="_blank" rel="noopener noreferrer">
              <img src={DellLogo} alt="Dell Logo" />
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </a>
            <a className="brand-row" href="https://www.equinix.com/partners/hpe" target="_blank" rel="noopener noreferrer">
              <img src={HpeLogo} alt="HPE Logo" />
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </a>
          </div>
        ),
        false: (
          <div className="metros-container">
            {phase?.metros?.map((metro) => (
              <div className="metro-row" key={`core-metro-${metro?.id?.value || metro?.code?.value || metro?.name}`}>
                <CheckCircleIcon width={30} height={30} viewBox="-2 -2 18 18" color="#7739D9" />
                {metro.name}
              </div>
            ))}
          </div>
        ),
      },
      explanation:
        'Based on your user distribution and cloud usage, we recommend concentrating your core infrastructure in the metro locations in the design to optimize latency, performance, and regulatory alignment (large white or blue-green dots). The smaller blue-green dots represent other Equinix locations that could serve as alternatives or additions to the recommended ones.',
    },
    'Interconnections': {
      header: {
        true: 'Connect clouds and locations through private interconnection',
        false: 'Cloud on-ramps to clouds you use at Equinix core metro locations',
      },
      content: {
        true: (
          <div>
            <h4>Recommended approach</h4>
            <p>
              Use a private, software-defined interconnection layer to:
              <ul>
                <li>Connect cloud environments directly</li>
                <li>Avoid public internet variability</li>
                <li>Scale connectivity as demand changes</li>
              </ul>
              This model can be implemented using Equinix Fabric, which provides on-demand, private connectivity between clouds, partners, and locations.
            </p>
            <h4>Public clouds, private access</h4>
            <p>
              With access to more than 220 cloud on-ramps—twice as many as the nearest competitor—Equinix holds nearly 40% of the global market share for cloud
              on-ramps. This empowers you to connect to the clouds and locations you need with advanced speed, security and reliability. Learn more about our
              strategic partnerships with leading cloud service providers to deliver secure, low-latency cloud access.
            </p>
            <a className="brand-row" href="https://www.equinix.com/partners/microsoft-azure" target="_blank" rel="noopener noreferrer">
              <div className="logo-name">
                <img src={azBadge} alt="Microsoft Azure Logo" />
                Microsoft Azure
              </div>
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </a>
            <a className="brand-row" href="https://www.equinix.com/partners/aws" target="_blank" rel="noopener noreferrer">
              <div className="logo-name">
                <img src={awsBadge} alt="Amazon Web Services Logo" />
                Amazon Web Services
              </div>
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </a>
            <a className="brand-row" href="https://www.equinix.com/partners/google-cloud" target="_blank" rel="noopener noreferrer">
              <div className="logo-name">
                <img src={gcpBadge} alt="Google Cloud Logo" />
                Google Cloud
              </div>
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </a>
            <a className="brand-row" href="https://www.equinix.com/partners/oracle" target="_blank" rel="noopener noreferrer">
              <div className="logo-name">
                <img src={ociBadge} alt="Oracle Cloud Logo" />
                Oracle
              </div>
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </a>
          </div>
        ),
        false: (
          <div className="metros-container">
            {phase?.metros?.map((metro) => {
              const metroCode = metro.code.value;
              const matchingOnRamps = onRampsMetros?.filter((onRamp) => onRamp.metroCode.value === metroCode);
              const cloudsByCount = matchingOnRamps?.reduce((acc, onRamp) => {
                acc[onRamp.cloud] = (acc[onRamp.cloud] || 0) + onRamp.eqxCenters;
                return acc;
              }, {});
              const cloudEntries = Object.entries(cloudsByCount);
              const metroKey = metro?.id?.value || metroCode || metro?.name;

              if (cloudEntries.length === 0) {
                return null;
              }

              return (
                <div className="metro-information" key={`metro-clouds-${metroKey}`}>
                  <div className="metro-header">{String(metro.name).toUpperCase()}</div>
                  {!!cloudEntries.length && (
                    <div className="metro-table">
                      {cloudEntries.map(([cloud, count]) => (
                        <div className="row" key={`${metroKey}-${cloud}`}>
                          <div className="logo">
                            <img src={onRamps[cloud].logo} alt={`${onRamps[cloud].name} Logo`} /> {onRamps[cloud].name}
                          </div>
                          <div className="count">{count}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ),
      },
      explanation:
        'As your infrastructure becomes more distributed, traditional network architectures struggle to efficiently connect cloud environments and locations. Connectivity to your clouds via on-ramps and between recommended deployments through private interconnection is visualized with monthly average round-trip times provided to indicate expected latency on routes.',
    },
    'Edge Locations': {
      header: { true: 'Extend reach to edge markets efficiently', false: 'Equinix edge metro locations to consider' },
      content: {
        true: (
          <div>
            <h4>Recommended approach</h4>
            <p>Use virtual infrastructure to extend network capabilities into these markets without the cost and complexity of physical deployments.</p>
            <h4>How this is implemented</h4>
            <p>
              This approach can be implemented using software-defined infrastructure that allows you to deploy and manage network capabilities on demand. With a
              built-in cloud router and support for virtual network functions from more than 15 major vendors, Equinix makes it possible for you to deploy
              virtual network infrastructure quickly and efficiently through:
              <ul>
                <li>Equinix Customer Portal</li>
                <li>API</li>
                <li>
                  AI-driven automation with{' '}
                  <a href="https://www.equinix.com/product-solutions/digital-infrastructure-services/equinix-fabric" target="_blank" rel="noopener noreferrer">
                    Fabric Intelligence
                  </a>
                </li>
                <li>Infrastructure-as-code solutions e.g. Terraform, Pulumi</li>
              </ul>
              This flexibility allows you to effortlessly scale operations and meet the demands of smaller, remote and new markets.
            </p>
          </div>
        ),
        false: (
          <div className="metros-container">
            {phase?.metros
              ?.filter((metro) => metro.notes === 'Edge')
              .map((metro) => (
                <div className="metro-row" key={`edge-metro-${metro?.id?.value || metro?.code?.value || metro?.name}`}>
                  <CheckCircleIcon width={30} height={30} viewBox="-2 -2 18 18" color="#7739D9" />

                  {metro.name}
                </div>
              ))}
          </div>
        ),
      },
      explanation:
        'Not all markets justify physical infrastructure deployment, given the significant capital expenditure, contractual obligations, and staffing needs. However, user demand in these regions still requires low-latency access and localized traffic handling.',
    },
    'Low Latency': {
      header: { true: 'Design for low-latency performance', false: 'Example latency' },
      content: {
        true: (
          <div>
            <h4>Recommended approach</h4>
            <p>Position infrastructure within close proximity to users and data sources, and ensure high-performance connectivity between locations.</p>
            <h4>Speed, scale and performance across the globe</h4>
            <p>
              Equinix’s network of high-performance data centers spans 77 major metropolitan areas around the globe, delivering access to most of the world’s
              internet users in less than 10 ms.
            </p>
          </div>
        ),
        false: (
          <div>
            {lowLatencyRows.length > 0 && <h4>To and from clouds you use, closest region</h4>}
            {lowLatencyRows.map((serviceLatencyRow) => (
              <div className="latency-row" key={`latency-${serviceLatencyRow.id}`}>
                <div className="row">
                  <div className="icon-and-text">{serviceLatencyRow.metroName}</div>
                  <DoubleArrowIcon width={22} height={24} viewBox="0 -7 17 24" />
                  <div className="icon-and-text">
                    {/* {serviceLatencyRow.csp?.logo && <img src={serviceLatencyRow.csp.logo} alt={`${serviceLatencyRow.csp.name} Logo`} />} */}
                    {serviceLatencyRow.csp?.name || serviceLatencyRow.serviceName}
                  </div>
                </div>
                <div className="latency">{serviceLatencyRow.totalLatency} ms</div>
              </div>
            ))}
            <h4>Between Equinix metro locations</h4>
            {hqMetroLatencies.length ? (
              hqMetroLatencies.map((to) => {
                const key = `${hqMetro?.id?.value || hqMetro?.name}-${to.metro?.id?.value || to.metro?.name}`;
                return (
                  <div className="latency-row" key={key}>
                    <div className="text">
                      {String(hqMetro.name).toUpperCase()}
                      <DoubleArrowIcon width={22} height={24} viewBox="0 -7 17 24" />
                      {String(to.metro.name).toUpperCase()}
                    </div>
                    <div className="latency">{to.distance} ms</div>
                  </div>
                );
              })
            ) : (
              <div className="latency-table">
                <div className="latency-value">No connected metros</div>
              </div>
            )}
          </div>
        ),
      },
      explanation:
        'Many modern workloads, particularly AI inference and real-time applications, are highly sensitive to latency. The green circles indicate an estimated 20 ms latency radius around the recommended deployments to give you a better view of the resulting low-latency coverage.',
    },
    'Potential Expansion': {
      header: { true: 'Expand into locations where your ecosystem is most active', false: 'Recommended locations based on industry and ecosystem alignment' },
      content: {
        true: (
          <div>
            <h4>Implication</h4>
            <p>
              Expanding into these ecosystem hubs increases access to partners and services, reduces integration complexity, and accelerates time to value for
              new initiatives.
            </p>
            <h4>Recommended approach</h4>
            <p>
              Prioritize expansion into locations where your ecosystem is already active to improve connectivity, enable faster collaboration, and support new
              growth opportunities.
            </p>
            <h4>Ecosystem density at these locations</h4>
            <p>
              These locations host a high concentration of enterprises, cloud providers, and network services, creating a dense environment for interconnection
              and collaboration. Equinix is the essential meeting ground where industries, platforms and partners converge, including:
              <ul>
                <li>60% of the Fortune 500</li>
                <li>More than one-third of the Forbes Global 2000</li>
                <li>5,500+ enterprises</li>
                <li>~3,000 cloud & IT service providers</li>
                <li>2,000+ network service providers</li>
                <li>~400 content & digital media companies</li>
              </ul>
              All securely, strategically and intricately linked by more than 507,000 interconnections.
            </p>
          </div>
        ),
        false: (
          <div className="metros-container">
            {phase?.metros
              ?.filter((metro) => getMetroNotes(metro).includes('EXPANSION'))
              .map((metro) => {
                const metroCode = getMetroCode(metro);
                const phaseMetroId = getMetroIdValue(metro);
                const metroWithMarketplace =
                  phaseCatalogMetros.find((catalogMetro) => getMetroIdValue(catalogMetro) === phaseMetroId) ||
                  phaseCatalogMetros.find((catalogMetro) => getMetroCode(catalogMetro) === metroCode) ||
                  metro;
                const marketplaceDensityRows = getMarketplaceDensityRows(metroWithMarketplace);
                const matchingOnRamps = onRampsMetros?.filter((onRamp) => {
                  const onRampMetroCode = onRamp?.metroCode?.value || onRamp?.metroCode;
                  return onRampMetroCode === metroCode;
                });
                const cloudsByCount = matchingOnRamps?.reduce((acc, onRamp) => {
                  acc[onRamp.cloud] = (acc[onRamp.cloud] || 0) + onRamp.eqxCenters;
                  return acc;
                }, {});
                const cloudEntries = Object.entries(cloudsByCount || {});
                const metroKey = metro?.id?.value || metroCode || metro?.name;

                return (
                  <div className="metro-information" key={`metro-expansion-${metroKey}`}>
                    <div className="metro-header">{String(metro.name).toUpperCase()}</div>
                    {!!cloudEntries.length && (
                      <>
                        <div className="sub-header">Cloud on-ramps to clouds you use:</div>
                        <div className="metro-table">
                          {cloudEntries.map(([cloud, count]) => (
                            <div className="row" key={`${metroKey}-${cloud}`}>
                              <div className="logo">
                                <img src={onRamps[cloud].logo} alt={`${onRamps[cloud].name} Logo`} /> {onRamps[cloud].name}
                              </div>
                              <div className="count">{count}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    {!!marketplaceDensityRows.length && (
                      <>
                        <div className="sub-header">Equinix ecosystem service density:</div>
                        <div className="metro-table">
                          {marketplaceDensityRows.map((density) => (
                            <div className="row" key={`${metroKey}-density-${density.name}`}>
                              <div className="logo">{density.name}</div>
                              <div className="count">{density.count}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
          </div>
        ),
      },
      explanation:
        'Based on your industry and strategic priorities, the heat map and following locations represent where your partners, providers, and customers are most densely interconnected.',
    },
    'Contact us': {
      header: { true: 'Review and refine your architecture' },
      content: {
        true: (
          <div>
            <p>
              Contact us to explore your tailored interconnected digital infrastructure design recommendation and how your business can overcome complexity and
              drive digital innovation with Equinix.
            </p>
            <h4>Learn more</h4>
            <Button
              text={'Equinix Networking Solutions'}
              isStretch
              onClick={() => window.open('https://www.equinix.com/product-solutions/networking', '_blank')}
            />
            <Button
              text={'Equinix for Network Architects'}
              isStretch
              onClick={() => window.open('https://www.equinix.com/teams/network-architects', '_blank')}
            />
            <Button
              text={'Get started with Fabric interconnection'}
              isStretch
              onClick={() => window.open('https://www.equinix.com/product-solutions/connectivity/fabric', '_blank')}
            />
            <Button
              text={'Try Network Edge free trial'}
              isStretch
              onClick={() => window.open('https://docs.equinix.com/network-edge/billing/ne-free-trial', '_blank')}
            />

            <h4>Appendix</h4>
            <div className="white-button">
              <Button text={'My Assessment Answers'} isStretch onClick={() => setPanelIndex(appendixSectionIndex)} />
            </div>
          </div>
        ),
      },
      isSingleSection: true,
    },
    'Appendix': {
      header: { true: 'Appendix' },
      content: {
        true: (
          <div className="appendix-list">
            {liteForm ? (
              appendixQuestions.map(({ question, answer, isMultiline }, index) => (
                <div key={question}>
                  <p className="appendix-question-number">{`Question ${index + 1}`}</p>
                  <h4>{question}</h4>
                  <div className="appendix-answer-table">
                    {formatAnswerRows(answer, isMultiline).map((item, itemIndex) => (
                      <div className="appendix-answer-row" key={`${question}-${itemIndex}`}>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p>No assessment responses available.</p>
            )}
          </div>
        ),
      },
      isSingleSection: true,
    },
  };

  const getSectionKeyForPhase = (phaseName, phaseIndex) => {
    if (phaseName && sections[phaseName]) {
      return phaseName;
    }
    const fallbackKey = phaseSectionOrder[phaseIndex]?.key;
    return sections[fallbackKey] ? fallbackKey : phaseSectionOrder[0].key;
  };

  const HandleBack = () => {
    if (isSingleSection) {
      const prevIndex = Math.max(panelIndex - 1, 0);
      setPanelIndex(prevIndex);
      setIsFirstSection(true);
      return;
    }
    if (isFirstSection) {
      const prevIndex = Math.max(panelIndex - 1, 0);
      setPanelIndex(prevIndex);
      if (prevIndex === 0) {
        setIsFirstSection(true);
      } else {
        setIsFirstSection(false);
      }
      return;
    }
    setIsFirstSection(true);
  };

  const HandleContinue = () => {
    if (isSingleSection) {
      const nextIndex = Math.min(panelIndex + 1, panelMaxIndex);
      setPanelIndex(nextIndex);
      setIsFirstSection(true);
      return;
    }
    if (isFirstSection) {
      setIsFirstSection(false);
      return;
    }
    const nextIndex = Math.min(panelIndex + 1, panelMaxIndex);
    setPanelIndex(nextIndex);
    setIsFirstSection(true);
  };

  const activeSectionKey = isIntroSection
    ? 'Introduction'
    : isContactSection
    ? 'Contact us'
    : isAppendixSection
    ? 'Appendix'
    : getSectionKeyForPhase(phases[panelPhaseIndex]?.name, panelPhaseIndex);
  const activeSection = sections[activeSectionKey];
  const activeHeader = activeSection?.header?.[isFirstSection] ?? activeSection?.header?.true;
  const activeContent = activeSection?.content?.[isFirstSection] ?? activeSection?.content?.true;
  const phaseNameLabel = isIntroSection || isContactSection || isAppendixSection ? activeSectionKey : formatPhaseMenuName(phases[panelPhaseIndex]?.name);

  useEffect(() => {
    if (!onMapFocusChange) return;
    if (panelPhaseId && panelPhaseId !== activePhaseId) return;

    const sectionFocusDefinition = sectionMapFocusConfig[activeSectionKey];
    const sectionFocus =
      sectionFocusDefinition && sectionFocusDefinition.mode
        ? sectionFocusDefinition
        : sectionFocusDefinition?.[isFirstSection] || sectionFocusDefinition?.true || { mode: 'all' };
    const normalizedFocus =
      sectionFocus?.mode === 'metroCodes' && (!sectionFocus.metroCodes || sectionFocus.metroCodes.length === 0) ? { mode: 'all' } : sectionFocus;

    onMapFocusChange({
      ...normalizedFocus,
      sourceKey: `${activeSectionKey}-${panelIndex}-${isFirstSection}-${activePhaseId || 'none'}`,
    });
  }, [activeSectionKey, panelIndex, isFirstSection, panelPhaseId, activePhaseId, onMapFocusChange, sectionMapFocusConfig]);

  return (
    <div className={classNames('lite-panel', { 'is-loading': phaseLoading })}>
      {phaseLoading && (
        <div className="loading-overlay" role="status" aria-live="polite">
          <div className="loading-spinner" />
          <div className="loading-text">Loading phase...</div>
        </div>
      )}
      <div className="phase-information">
        <div className="phase-name">
          <Menu
            menuClassName={'default-menu'}
            align={'start'}
            menuButton={
              <MenuButton className="phase-name-button">
                <HamburgerMenuIcon /> {phaseNameLabel}
                <div className="triangle-container" data-testid={'triangle-icon'}>
                  <TriangleIcon width={6} height={5} color="black" />
                </div>
              </MenuButton>
            }
            transition
            portal>
            <MenuItem onClick={() => setPanelIndex(0)} disabled={isIntroSection}>
              Introduction
            </MenuItem>
            {phases.map((phaseItem) => (
              <MenuItem
                key={phaseItem.id.value}
                onClick={() => handlePhaseSelect(phaseItem.id.value)}
                disabled={!isSingleSection && phaseItem.id.value === activePhaseId}>
                {formatPhaseMenuName(phaseItem.name)}
              </MenuItem>
            ))}
            <MenuItem onClick={() => setPanelIndex(contactSectionIndex)} disabled={isContactSection}>
              Contact us
            </MenuItem>
            <MenuItem onClick={() => setPanelIndex(appendixSectionIndex)} disabled={isAppendixSection}>
              Appendix
            </MenuItem>
          </Menu>
        </div>
        <div className="progress-bar" role="list" aria-label="Phase progress">
          {phases.map((phaseItem, index) => {
            let status = 'upcoming';
            if (isContactSection || isAppendixSection) {
              status = 'complete';
            } else if (!isIntroSection) {
              status = index === panelPhaseIndex ? 'current' : index < panelPhaseIndex ? 'complete' : 'upcoming';
            }
            return (
              <span
                key={phaseItem.id.value}
                className={classNames('progress-dot', `progress-dot--${status}`)}
                role="listitem"
                aria-current={status === 'current' ? 'step' : undefined}
              />
            );
          })}
        </div>
      </div>
      <div className="header" key={`header-${panelIndex}-${isFirstSection}`}>
        {activeHeader}
      </div>
      <div className="content-container" key={`content-${panelIndex}-${isFirstSection}`}>
        {isFirstSection && !!activeSection?.explanation && <div className="explanation-box">{activeSection?.explanation}</div>}
        <div className="content">{activeContent}</div>
      </div>
      <div className="buttons-container">
        <Button text={'Back'} color="gray" isInverse onClick={HandleBack} isDisabled={panelIndex === 0} />
        {!isAppendixSection && !isContactSection && panelIndex < panelMaxIndex && <Button text={'Continue →'} onClick={HandleContinue} />}
      </div>
    </div>
  );
};

export default LitePanel;
